import { Info, Plus, Trash2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  DEFAULT_ORGANISATION_SETTINGS,
  fxInForce,
  fxPairKey,
  fxPairsOf,
  fxRateSchema,
  fxUpcoming,
  rateDifference,
  reciprocalRate,
  type CurrencyCode,
  type FxPair,
  type FxPairRate,
  type FxRateView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Card } from '../../../components/Card';
import { DatePicker } from '../../../components/DatePicker';
import { Field } from '../../../components/Field';
import { Input } from '../../../components/Input';
import { Segmented } from '../../../components/Segmented';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { errorMessage } from '../../../lib/api-client';
import { useAppSettings } from '../../../lib/app-settings';
import { currencyLabel } from '../../../lib/currency-label';
import { EMPTY, formatIsoDate } from '../../../lib/format';
import { useCanEdit, useCanView, useCurrentSession } from '../../../stores/session';
import { useToasts } from '../../../stores/toasts';
import { isPendingRate, useAddRate, useFxRates, useRemoveRate, useRestoreRate } from '../api/settings-api';
import { RateStepChart } from '../components/RateStepChart';
import styles from './FxRatesPage.module.css';

type Range = '3m' | '6m' | 'all';
const RANGES: readonly { readonly value: Range; readonly label: string }[] = [
  { value: '3m', label: '3 months' },
  { value: '6m', label: '6 months' },
  { value: 'all', label: 'All' },
];
const RANGE_MONTHS: Readonly<Record<Exclude<Range, 'all'>, number>> = { '3m': 3, '6m': 6 };
const RANGE_WORDS: Readonly<Record<Range, string>> = { '3m': 'last 3 months', '6m': 'last 6 months', all: 'every rate' };

function todayIn(timeZone: string): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone }).format(new Date());
}

function monthsBefore(isoDay: string, months: number): string {
  const day = new Date(`${isoDay}T00:00:00Z`);
  day.setUTCMonth(day.getUTCMonth() - months);
  return day.toISOString().slice(0, 10);
}

/** "1 USD = 122.5 BDT". */
function rateWords(base: string, rate: string, quote: string): string {
  return `1 ${base} = ${rate} ${quote}`;
}

/**
 * The FX table (D13: typed by hand, no rate feed; Admin redesign 2026-11-02, Option A of `5-settings/exchange-rates.html`):
 * the pairs on the left; the pair in view with its rate today, a step chart and every rate in the middle; adding a rate
 * on the right. Amounts are never stored converted (#7): "Show converted" on reports uses the rate in force on each
 * amount's own date (Q21). A rate works both ways, so one typed BDT→USD belongs to the USD→BDT pair.
 */
export function FxRatesPage(): ReactNode {
  const canView = useCanView('admin_config');
  const rates = useFxRates(canView);
  const settings = useAppSettings();
  const [picked, setPicked] = useState<string | null>(null);
  // Review W20: a refused add hands back what was typed. The form may have moved to the new pair and back meanwhile
  // (it starts again per pair), so the draft is kept here and the form opens on it; `round` starts it again even when
  // the pair in view is the same.
  const [returned, setReturned] = useState<{ readonly draft: RateDraft; readonly round: number } | null>(null);
  // Held here, not in the form or the row: the form starts again on the pair a rate was added to, and a removed row
  // leaves the screen at once, yet the server's answer (and Undo) must still land.
  const add = useAddRate();
  const remove = useRemoveRate();
  const restore = useRestoreRate();
  const show = useToasts((state) => state.show);

  // Undo, not a confirm (owner, 2026-11-02): the rate goes at once; Undo brings the same rate back.
  const removeRate = (stored: FxRateView): void => {
    const typed = rateWords(stored.fromCurrency, stored.rate, stored.toCurrency);
    remove.mutate(stored, { onError: (cause) => show({ tone: 'danger', message: "The rate wasn't removed", detail: errorMessage(cause) }) });
    show({
      tone: 'info',
      message: 'Rate removed',
      detail: `${typed} from ${formatIsoDate(stored.effectiveFrom)}.`,
      action: { label: 'Undo', run: () => restore.mutate(stored, { onError: (cause) => show({ tone: 'danger', message: "The rate couldn't come back", detail: errorMessage(cause) }) }) },
    });
  };

  if (!canView) return <ForbiddenState resource="the exchange rates" contact="Your admin" />;
  if (rates.isPending) return <SkeletonLines count={8} />;
  if (rates.error) return <ServerErrorState onRetry={() => void rates.refetch()} />;
  const reporting = settings.data?.settings.reportingCurrency ?? 'BDT';
  const currencies: readonly CurrencyCode[] = settings.data?.settings.currencies ?? [];
  const pairs = fxPairsOf(rates.data, reporting);
  // The pair in view goes when its last rate does; the first pair takes its place.
  const pair = pairs.find((candidate) => candidate.key === picked) ?? pairs[0] ?? null;
  // Review W20: a rate starts on a day of the organisation's calendar, not the viewer's (as on the server).
  const zone = settings.data?.settings.defaultTimezone ?? DEFAULT_ORGANISATION_SETTINGS.defaultTimezone;
  const today = todayIn(zone);
  const pick = (key: string): void => {
    setReturned(null);
    setPicked(key);
  };
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Exchange rates</h1>
        <p className={styles.lede}>
          Used only when a report is switched to “Show converted”. Each amount is converted at the rate in force on its own date, so add a new rate when it changes
          rather than editing the old one.
        </p>
      </header>
      <div className={styles.layout}>
        <PairList pairs={pairs} current={pair} today={today} currencies={currencies} onPick={pick} />
        <div className={styles.middle}>{pair ? <PairDetail pair={pair} today={today} onRemove={removeRate} /> : <NoRates />}</div>
        <div className={styles.side}>
          <AddRate
            key={`${pair?.key ?? 'none'}:${returned?.round ?? 0}`}
            add={add}
            pair={pair}
            pairs={pairs}
            currencies={currencies}
            reporting={reporting}
            today={today}
            zone={zone}
            returned={returned?.draft ?? null}
            onAdded={(from, to) => pick(fxPairKey(from, to))}
            onRefused={(draft) => {
              setPicked(fxPairKey(draft.from, draft.to));
              setReturned((current) => ({ draft, round: (current?.round ?? 0) + 1 }));
            }}
          />
          <p className={styles.note}>
            <Info size={16} aria-hidden className={styles.noteIcon} />
            <span>Nothing stored is ever changed by a rate. A rate works both ways: USD to BDT also gives BDT to USD. Rates are typed by hand; there is no live feed.</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function NoRates(): ReactNode {
  return (
    <Card title="No rates yet">
      <p className={styles.muted}>Until one is added, amounts stay in their own currency on every report. Add the first one with the form beside this.</p>
    </Card>
  );
}

interface PairListProps {
  readonly pairs: readonly FxPair[];
  readonly current: FxPair | null;
  readonly today: string;
  readonly currencies: readonly CurrencyCode[];
  readonly onPick: (key: string) => void;
}

function PairList({ pairs, current, today, currencies, onPick }: PairListProps): ReactNode {
  return (
    <Card title="Pairs" meta={String(pairs.length)} isFlush>
      {pairs.length > 0 && (
        <ul className={styles.pairs}>
          {pairs.map((pair) => {
            const inForce = fxInForce(pair, today);
            const next = fxUpcoming(pair, today)[0];
            return (
              <li key={pair.key}>
                <button type="button" className={styles.pair} aria-current={pair.key === current?.key ? 'true' : undefined} onClick={() => onPick(pair.key)}>
                  <span className={styles.pairText}>
                    <span className={styles.pairName}>
                      {pair.base} → {pair.quote}
                    </span>
                    <span className={styles.meta}>{inForce ? `since ${formatIsoDate(inForce.effectiveFrom)}` : `from ${formatIsoDate(next?.effectiveFrom)}`}</span>
                  </span>
                  <span className={styles.pairRate}>{inForce?.rate ?? EMPTY}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
      <p className={styles.pairsNote}>
        A pair shows here once it has a rate. The form offers the currencies switched on in System settings{currencies.length > 0 ? `: ${listOf(currencies)}` : ''}.
      </p>
    </Card>
  );
}

function listOf(items: readonly string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`;
}

type RemoveRate = (stored: FxRateView) => void;

function PairDetail({ pair, today, onRemove }: { readonly pair: FxPair; readonly today: string; readonly onRemove: RemoveRate }): ReactNode {
  const [range, setRange] = useState<Range>('6m');
  const inForce = fxInForce(pair, today);
  const upcoming = fxUpcoming(pair, today);
  const before = inForce ? pair.rates[pair.rates.indexOf(inForce) + 1] : undefined;
  const from = range === 'all' ? null : monthsBefore(today, RANGE_MONTHS[range]);
  const oldestFirst = [...pair.rates].reverse();
  return (
    <>
      <Card title={`1 ${pair.base} in ${pair.quote}`} action={<Segmented label="How far back" value={range} options={RANGES} onChange={setRange} />}>
        <div className={styles.now}>
          <div className={styles.nowRate}>
            <span className={styles.big}>{inForce?.rate ?? EMPTY}</span>
            <span className={styles.meta}>{inForce ? `In force from ${formatIsoDate(inForce.effectiveFrom)}` : 'Not in force yet'}</span>
          </div>
          <div className={styles.nowChange}>
            {inForce && <span className={styles.muted}>{changeWords(inForce, before)}</span>}
            {upcoming.map((next) => (
              <span key={next.stored.id} className={styles.muted}>
                Then <strong className={styles.data}>{next.rate}</strong> from {formatIsoDate(next.effectiveFrom)}
              </span>
            ))}
          </div>
        </div>
        <RateStepChart rates={oldestFirst} from={from} today={today} title={`1 ${pair.base} in ${pair.quote}, ${RANGE_WORDS[range]}`} />
      </Card>
      <RateHistory pair={pair} onRemove={onRemove} />
    </>
  );
}

/** "Up 0.7 from 01-09-2026", worked out exactly from the two rates as typed. */
function changeWords(current: FxPairRate, before: FxPairRate | undefined): ReactNode {
  if (!before) return 'The first rate for this pair';
  const difference = rateDifference(current.rate, before.rate);
  const when = formatIsoDate(before.effectiveFrom);
  if (difference === null || difference === '0') return `No change from the rate of ${when}`;
  const isDown = difference.startsWith('-');
  return (
    <>
      {isDown ? 'Down' : 'Up'} <strong className={styles.data}>{isDown ? difference.slice(1) : difference}</strong> from {when}
    </>
  );
}

function RateHistory({ pair, onRemove }: { readonly pair: FxPair; readonly onRemove: RemoveRate }): ReactNode {
  const canEdit = useCanEdit('admin_config');
  return (
    <Card title={`All ${pair.base} → ${pair.quote} rates`} meta={`${pair.rates.length} · newest first`} isFlush>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th scope="col">Rate</th>
              <th scope="col">From</th>
              <th scope="col">Added by</th>
              {canEdit && (
                <th scope="col">
                  <span className={styles.srOnly}>Remove</span>
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {pair.rates.map((rate) => (
              <RateRow key={rate.stored.id} pair={pair} rate={rate} canEdit={canEdit} onRemove={onRemove} />
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

interface RateRowProps {
  readonly pair: FxPair;
  readonly rate: FxPairRate;
  readonly canEdit: boolean;
  readonly onRemove: RemoveRate;
}

function RateRow({ pair, rate, canEdit, onRemove }: RateRowProps): ReactNode {
  const typed = rateWords(rate.stored.fromCurrency, rate.stored.rate, rate.stored.toCurrency);
  // A rate still on its way to the server has no id to remove yet.
  const isPending = isPendingRate(rate.stored);
  return (
    <tr data-pending={isPending || undefined}>
      <td>
        <span className={styles.rateCell}>
          <strong>
            1 {pair.base} = <span className={styles.data}>{rate.rate}</span> {pair.quote}
          </strong>
          {rate.isReversed && <span className={styles.meta}>Typed as {typed}</span>}
        </span>
      </td>
      <td className={styles.data}>{formatIsoDate(rate.effectiveFrom)}</td>
      <td>{rate.stored.createdByName ?? EMPTY}</td>
      {canEdit && (
        <td className={styles.actionCell}>
          <Button size="sm" variant="quiet" icon={Trash2} disabled={isPending} aria-label={`Remove ${typed} from ${formatIsoDate(rate.effectiveFrom)}`} onClick={() => onRemove(rate.stored)}>
            Remove
          </Button>
        </td>
      )}
    </tr>
  );
}

/** What the Add form held when the server refused it. */
interface RateDraft {
  readonly from: CurrencyCode;
  readonly to: CurrencyCode;
  readonly rate: string;
  readonly effectiveFrom: string;
  readonly error: string;
}

interface AddRateProps {
  readonly add: ReturnType<typeof useAddRate>;
  readonly pair: FxPair | null;
  readonly pairs: readonly FxPair[];
  readonly currencies: readonly CurrencyCode[];
  readonly reporting: CurrencyCode;
  readonly today: string;
  /** The organisation's zone, which the date picker's "today" reads in. */
  readonly zone: string;
  /** A refused add's draft, which the form opens on. */
  readonly returned: RateDraft | null;
  readonly onAdded: (from: CurrencyCode, to: CurrencyCode) => void;
  readonly onRefused: (draft: RateDraft) => void;
}

/** "One of … is worth … of … from …". It starts on the pair in view and today, and says the reverse as you type. */
function AddRate({ add, pair, pairs, currencies, reporting, today, zone, returned, onAdded, onRefused }: AddRateProps): ReactNode {
  const canEdit = useCanEdit('admin_config');
  const show = useToasts((state) => state.show);
  const myName = useCurrentSession()?.fullName ?? null;
  const [from, setFrom] = useState<string | null>(() => returned?.from ?? pair?.base ?? currencies.find((code) => code !== reporting) ?? null);
  const [to, setTo] = useState<string | null>(() => returned?.to ?? pair?.quote ?? reporting);
  const [rate, setRate] = useState(returned?.rate ?? '');
  const [effectiveFrom, setEffectiveFrom] = useState(returned?.effectiveFrom ?? today);
  const [error, setError] = useState<string | null>(returned?.error ?? null);
  if (!canEdit) return null;
  // A pair's own currencies stay offered even if one has been switched off since.
  const codes = [...new Set<string>([...currencies, ...(pair ? [pair.base, pair.quote] : [])])];
  const options = codes.map((code) => ({ value: code, label: currencyLabel(code) }));
  const reverse = from !== null && to !== null && from !== to ? reciprocalRate(rate) : null;

  const submit = (): void => {
    const parsed = fxRateSchema.safeParse({ fromCurrency: from, toCurrency: to, rate, effectiveFrom });
    if (!parsed.success) {
      setError(parsed.error.issues[0]?.message ?? 'Check the rate.');
      return;
    }
    const input = parsed.data;
    // The same rule the server keeps, said before sending: one rate per pair and day, whichever way it was typed.
    const taken = pairs.find((candidate) => candidate.key === fxPairKey(input.fromCurrency, input.toCurrency))?.rates.find((row) => row.effectiveFrom === input.effectiveFrom);
    if (taken) {
      setError(`${rateWords(taken.stored.fromCurrency, taken.stored.rate, taken.stored.toCurrency)} already starts on ${formatIsoDate(input.effectiveFrom)}, and a rate works both ways. Remove it first, or pick another date.`);
      return;
    }
    setError(null);
    setRate('');
    onAdded(input.fromCurrency, input.toCurrency);
    add.mutate(
      { ...input, createdByName: myName },
      {
        onSuccess: () => show({ tone: 'success', message: 'Rate added', detail: `${rateWords(input.fromCurrency, input.rate, input.toCurrency)} from ${formatIsoDate(input.effectiveFrom)}.` }),
        onError: (cause) => {
          // Taken out again; what was typed comes back with the reason, on the pair it was typed for (the form may have
          // started again since). The toast says it too.
          onRefused({ from: input.fromCurrency, to: input.toCurrency, rate: input.rate, effectiveFrom: input.effectiveFrom, error: errorMessage(cause) });
          show({ tone: 'danger', message: "The rate wasn't added", detail: errorMessage(cause) });
        },
      },
    );
  };

  return (
    <Card title="Add a rate">
      <form
        className={styles.form}
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <Field label="One of">
          {({ inputId }) => <Select id={inputId} label="One of" options={options} value={from} onChange={setFrom} />}
        </Field>
        <Field label="Is worth" error={error ?? undefined}>
          {({ inputId, describedBy, isInvalid }) => (
            <Input id={inputId} aria-describedby={describedBy} isInvalid={isInvalid} inputMode="decimal" value={rate} placeholder="e.g. 122.5" onChange={(event) => setRate(event.target.value.trim())} />
          )}
        </Field>
        <Field label="Of">
          {({ inputId }) => <Select id={inputId} label="Of" options={options} value={to} onChange={setTo} />}
        </Field>
        <Field label="From">
          {({ inputId }) => <DatePicker id={inputId} label="From" value={effectiveFrom} onChange={setEffectiveFrom} timeZone={zone} />}
        </Field>
        <p className={styles.reverse} aria-live="polite">
          {reverse !== null ? (
            <>
              So <strong className={styles.data}>{rateWords(to ?? '', reverse, from ?? '')}</strong> from the same day.
            </>
          ) : (
            'Type a rate to see it the other way round.'
          )}
        </p>
        <Button type="submit" variant="solid" icon={Plus} disabled={rate === '' || from === null || to === null}>
          Add rate
        </Button>
      </form>
    </Card>
  );
}
